import React from 'react';
import { Box, Group, Stack, Text, UnstyledButton } from '@mantine/core';

import { When } from '../../../common';

export const BATCH_STAT_COLUMN_WIDTHS = {
    metric: 56,

    proof: 260,
} as const;

export type StatPillProps = {
    label: string;
    value: React.ReactNode;
    metric?: boolean;
    trailing?: React.ReactNode;
    width?: number | string;
    onClick?: (e: React.MouseEvent) => void;
    truncateValue?: boolean;
    nowrap?: boolean;
};

function getStatPillValueClassName(
    nowrap: boolean,
    truncateValue: boolean,
): string {
    if (nowrap) {
        return 'whitespace-nowrap text-gray-900';
    }
    if (truncateValue) {
        return 'min-w-0 flex-1 truncate text-gray-900';
    }
    return 'min-w-0 flex-1 whitespace-normal break-words leading-snug text-gray-900 line-clamp-2';
}

export function StatPill({
    label,
    value,
    metric = false,
    trailing,
    width,
    onClick,
    truncateValue = true,
    nowrap = false,
}: StatPillProps) {
    const valueTitle =
        typeof value === 'string' || typeof value === 'number'
            ? String(value)
            : undefined;

    const labelNode = (
        <Text size="xs" c="dimmed" className="uppercase tracking-wider">
            {label}
        </Text>
    );

    const valueNode = metric ? (
        <Text
            size="lg"
            fw={700}
            lh={1.2}
            className="tabular-nums whitespace-nowrap text-gray-900"
            title={valueTitle}
        >
            {value}
        </Text>
    ) : (
        <Group gap={6} wrap="nowrap" align="center" className="w-full">
            <Text
                size="sm"
                fw={600}
                title={valueTitle}
                className={getStatPillValueClassName(nowrap, truncateValue)}
            >
                {value}
            </Text>
            {trailing}
        </Group>
    );

    const content = (
        <Box
            px="sm"
            py={4}
            w={metric ? 'auto' : width}
            miw={metric ? width : undefined}
            className={metric || nowrap ? 'shrink-0' : 'min-w-0'}
        >
            <Stack gap={6} align="stretch">
                {labelNode}
                {valueNode}
            </Stack>
        </Box>
    );

    return (
        <>
            <When condition={!!onClick}>
                <UnstyledButton onClick={onClick} className="cursor-pointer">
                    {content}
                </UnstyledButton>
            </When>
            <When condition={!onClick}>{content}</When>
        </>
    );
}
